import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
// eslint-disable-next-line no-restricted-imports
import type { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import type { Location } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridCellParams, GridColDef, GridRowModel, useGridApiRef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { CharterLoyaltyCabinClass } from '~/components/Flights/Charter/charter.types';

import { charterLoyaltyPointMultiplierQueryKeys } from '~/hooks/apis/flights-charter/charterLoyaltyPointMultiplierQueryKeys';
import {
  useGetCharterLoyaltyPointMultipliers,
  useUpdateCharterLoyaltyPointMultipliers,
} from '~/hooks/apis/flights-charter/useCharterLoyaltyPointMultipliers';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import * as TimeService from '~/services/TimeService';

import LoyaltyMultiplierEditCell from './LoyaltyPoints/LoyaltyMultiplierEditCell';
import {
  LoyaltyRow,
  VALID_MULTIPLIERS,
  buildUpdatePayload,
  mapResultsToRows,
  mergeSavedMultipliers,
  updateDraft,
} from './LoyaltyPoints/loyaltyDraft';
import { useUnsavedChangesGuard } from './hooks/useUnsavedChangesGuard';

type AirportPairEntry = {
  depAirport: string;
  arrAirport: string;
};

type FormState = {
  departureDateFrom: Date | null;
  departureDateTo: Date | null;
  cabinClass: Array<CharterLoyaltyCabinClass>;
  airportFilterEnabled: boolean;
  airportPairs: Array<AirportPairEntry>;
};

type SearchFilters = {
  departureDateFrom: string;
  departureDateTo: string;
  cabinClass: Array<CharterLoyaltyCabinClass>;
  airportPairs?: Array<AirportPairEntry>;
};

const CABIN_CLASS_OPTIONS: Array<{ label: string; value: CharterLoyaltyCabinClass }> = [
  { label: 'Economy', value: 'ECONOMY' },
  { label: 'Premium Economy', value: 'PREMIUM_ECONOMY' },
  { label: 'Lux Row', value: 'LUX_ROW' },
  { label: 'Lux Row (Pax One)', value: 'LUX_ROW_PAX_ONE' },
  { label: 'Business', value: 'BUSINESS' },
];

const allCabinClasses = CABIN_CLASS_OPTIONS.map((option) => option.value);

const AIRPORT_OPTIONS = ['MLE', 'MEL'] as const;

const DEFAULT_DEPARTURE_DATE_FROM = new Date(2026, 4, 18);
const DEFAULT_DEPARTURE_DATE_TO = new Date(2026, 11, 31);
const DEFAULT_SEARCH_FILTERS: SearchFilters = {
  departureDateFrom: TimeService.formatDateISO(DEFAULT_DEPARTURE_DATE_FROM),
  departureDateTo: TimeService.formatDateISO(DEFAULT_DEPARTURE_DATE_TO),
  cabinClass: allCabinClasses,
};
const MAX_AUTO_HEIGHT_ROWS = 5;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type PendingAction =
  | { type: 'search'; filters: SearchFilters }
  | { type: 'discard' }
  | { type: 'navigate'; location: Location };

export default function CharterLoyaltyPointsPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState<FormState>({
    departureDateFrom: DEFAULT_DEPARTURE_DATE_FROM,
    departureDateTo: DEFAULT_DEPARTURE_DATE_TO,
    cabinClass: allCabinClasses,
    airportFilterEnabled: false,
    airportPairs: [{ depAirport: AIRPORT_OPTIONS[0], arrAirport: AIRPORT_OPTIONS[1] }],
  });
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(DEFAULT_SEARCH_FILTERS);
  const [editedRows, setEditedRows] = useState<Map<string, LoyaltyRow>>(new Map());
  const editedRowsRef = useRef(editedRows);
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [baseRows, setBaseRows] = useState<Array<LoyaltyRow>>([]);
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({ page: 0, pageSize: 50 });

  const replaceDraft = useCallback((draft: Map<string, LoyaltyRow>) => {
    // Event handlers must see the latest committed cell before React's next render.
    editedRowsRef.current = draft;
    setEditedRows(draft);
  }, []);

  const loyaltyQuery = useGetCharterLoyaltyPointMultipliers(searchFilters);
  const updateMutation = useUpdateCharterLoyaltyPointMultipliers();
  const invalidDateFrom = !formState.departureDateFrom || Number.isNaN(formState.departureDateFrom.getTime());
  const invalidDateTo = !formState.departureDateTo || Number.isNaN(formState.departureDateTo.getTime());
  const invalidDateRange = Boolean(
    formState.departureDateFrom &&
    formState.departureDateTo &&
    !invalidDateFrom &&
    !invalidDateTo &&
    formState.departureDateFrom > formState.departureDateTo,
  );
  const draftFilters = useMemo<SearchFilters | null>(() => {
    const { departureDateFrom, departureDateTo } = formState;
    if (
      !departureDateFrom ||
      !departureDateTo ||
      invalidDateFrom ||
      invalidDateTo ||
      invalidDateRange ||
      !formState.cabinClass.length
    ) {
      return null;
    }
    return {
      departureDateFrom: TimeService.formatDateISO(departureDateFrom),
      departureDateTo: TimeService.formatDateISO(departureDateTo),
      cabinClass: formState.cabinClass,
      airportPairs: formState.airportFilterEnabled ? formState.airportPairs : undefined,
    };
  }, [formState, invalidDateFrom, invalidDateTo, invalidDateRange]);
  const filtersChanged = !draftFilters || JSON.stringify(draftFilters) !== JSON.stringify(searchFilters);
  const appliedFilterSummary = [
    `${TimeService.formatDateSlashes(searchFilters.departureDateFrom)}–${TimeService.formatDateSlashes(searchFilters.departureDateTo)}`,
    searchFilters.cabinClass.length === allCabinClasses.length
      ? 'All cabins'
      : searchFilters.cabinClass
          .map((cabinClass) => CABIN_CLASS_OPTIONS.find((option) => option.value === cabinClass)?.label ?? cabinClass)
          .join(', '),
    searchFilters.airportPairs?.length
      ? searchFilters.airportPairs.map((pair) => `${pair.depAirport} → ${pair.arrAirport}`).join(', ')
      : 'All routes',
  ].join(' · ');

  useEffect(() => {
    // A background response must not change the comparison baseline underneath a draft.
    if (loyaltyQuery.data && !editedRowsRef.current.size && !savingRef.current) {
      setBaseRows(mapResultsToRows(loyaltyQuery.data));
    }
  }, [loyaltyQuery.data, loyaltyQuery.dataUpdatedAt, searchFilters]);

  const onNavigationAttempt = useCallback(
    (location: Location) => {
      if (savingRef.current) {
        enqueueSnackbar('Please wait for the save to finish before leaving.', { variant: 'info' });
      } else {
        setPendingAction({ type: 'navigate', location });
      }
    },
    [enqueueSnackbar],
  );
  const navigationGuard = useUnsavedChangesGuard({
    hasUnsavedChanges: editedRows.size > 0 || isSaving,
    onNavigationAttempt,
  });

  const applySearch = useCallback(
    async (filters: SearchFilters) => {
      replaceDraft(new Map());
      setPage(0);
      setSearchFilters(filters);
      // Search is also an explicit refresh when the filters have not changed.
      if (JSON.stringify(filters) === JSON.stringify(searchFilters)) {
        const refreshed = await loyaltyQuery.refetch();
        setRefreshFailed(refreshFailed && refreshed.isError);
      } else {
        setRefreshFailed(false);
        setBaseRows([]);
      }
    },
    [loyaltyQuery, refreshFailed, replaceDraft, searchFilters, setPage],
  );

  const baseRowMap = useMemo(() => {
    const map = new Map<string, LoyaltyRow>();
    for (const row of baseRows) {
      map.set(row.flightInstanceId, row);
    }
    return map;
  }, [baseRows]);

  const rows = useMemo(() => {
    return baseRows.map((baseRow) => editedRows.get(baseRow.flightInstanceId) ?? baseRow);
  }, [baseRows, editedRows]);

  const handleSearch = useCallback(() => {
    if (savingRef.current || !draftFilters) return;
    if (editedRowsRef.current.size) {
      setPendingAction({ type: 'search', filters: draftFilters });
    } else {
      applySearch(draftFilters);
    }
  }, [applySearch, draftFilters]);

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<LoyaltyRow>) => {
      if (savingRef.current) return baseRowMap.get(newRow.flightInstanceId) ?? newRow;
      replaceDraft(
        updateDraft(editedRowsRef.current, newRow, baseRowMap.get(newRow.flightInstanceId), searchFilters.cabinClass),
      );

      return newRow;
    },
    [baseRowMap, replaceDraft, searchFilters],
  );

  const getCellClassName = useCallback(
    (params: GridCellParams<LoyaltyRow>) => {
      const field = params.field as CharterLoyaltyCabinClass;
      if (!searchFilters.cabinClass.includes(field)) {
        return '';
      }

      const original = baseRowMap.get(params.row.flightInstanceId);
      if (!original) {
        return '';
      }

      return params.value !== original[field] ? 'cell-modified' : '';
    },
    [baseRowMap, searchFilters],
  );

  const handleSave = useCallback(async () => {
    if (!editedRowsRef.current.size || savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      const draft = editedRowsRef.current;
      const payload = buildUpdatePayload(draft, searchFilters.cabinClass, baseRowMap);
      const savedItems = await updateMutation.mutateAsync(payload);
      // The write succeeded. Keep its confirmed values visible even if the read fails.
      setBaseRows((current) =>
        current.map((row) => {
          const saved = savedItems.find((item) => item.flightInstanceId === row.flightInstanceId);
          return saved
            ? {
                ...row,
                ...Object.fromEntries(saved.multipliers.map((item) => [item.cabinClass, item.loyaltyPointMultiplier])),
              }
            : row;
        }),
      );
      replaceDraft(new Map());
      enqueueSnackbar('Loyalty point multipliers saved successfully.', { variant: 'success' });
      const refreshed = await loyaltyQuery.refetch();
      if (refreshed.isError) {
        setRefreshFailed(true);
      } else if (refreshed.data) {
        // The immediate read can lag behind the write because the service uses a read replica.
        const confirmedResults = mergeSavedMultipliers(refreshed.data, savedItems);
        queryClient.setQueryData(charterLoyaltyPointMultiplierQueryKeys.list(searchFilters), confirmedResults);
        setRefreshFailed(false);
        setBaseRows(mapResultsToRows(confirmedResults));
      }
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Unable to save loyalty point multipliers.', {
        variant: 'error',
      });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [baseRowMap, enqueueSnackbar, loyaltyQuery, queryClient, replaceDraft, searchFilters, updateMutation]);

  const confirmDiscard = () => {
    if (!pendingAction || savingRef.current) return;
    replaceDraft(new Map());
    setPendingAction(null);
    if (pendingAction.type === 'search') applySearch(pendingAction.filters);
    if (pendingAction.type === 'navigate') navigationGuard.releaseAndNavigate(pendingAction.location);
  };

  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (!params.isEditable) {
        return;
      }
      const cellMode = apiRef.current.getCellMode(params.id, params.field);
      if (cellMode === 'view') {
        apiRef.current.startCellEditMode({ id: params.id, field: params.field });
      }
    },
    [apiRef],
  );

  const columns = useMemo<Array<GridColDef<LoyaltyRow>>>(() => {
    const staticColumns: Array<GridColDef<LoyaltyRow>> = [
      {
        field: 'depDate',
        headerName: 'Departure date',
        width: 130,
        valueFormatter: (value) => TimeService.formatDateSlashes(String(value)) || String(value),
      },
      {
        field: 'route',
        headerName: 'Route',
        width: 140,
        valueGetter: (_value, row) => `${row.depAirport} → ${row.arrAirport}`,
      },
    ];

    const multiplierColumns: Array<GridColDef<LoyaltyRow>> = searchFilters.cabinClass.map((cabinClass) => ({
      field: cabinClass,
      headerName: CABIN_CLASS_OPTIONS.find((option) => option.value === cabinClass)?.label ?? cabinClass,
      flex: 1,
      minWidth: 130,
      type: 'singleSelect',
      editable: !isSaving && !loyaltyQuery.isFetching,
      renderEditCell: (params) => <LoyaltyMultiplierEditCell {...params} />,
      valueOptions: [...VALID_MULTIPLIERS],
      align: 'center' as const,
      headerAlign: 'center' as const,
    }));

    return [...staticColumns, ...multiplierColumns];
  }, [isSaving, loyaltyQuery.isFetching, searchFilters]);

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={600}>
          Charter Loyalty Points
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search charter departures and manage loyalty point multipliers by cabin class.
        </Typography>
      </Stack>

      <Card variant="outlined">
        <CardHeader title="Search departures" sx={{ pb: 0 }} />
        <CardContent>
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <DatePicker
                  label="Departure date from"
                  value={formState.departureDateFrom ? TimeService.toDayJs(formState.departureDateFrom) : null}
                  onChange={(newValue: Dayjs | null) =>
                    setFormState((current) => ({ ...current, departureDateFrom: newValue ? newValue.toDate() : null }))
                  }
                  maxDate={
                    formState.departureDateTo && !invalidDateTo
                      ? TimeService.toDayJs(formState.departureDateTo)
                      : undefined
                  }
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: invalidDateFrom,
                      helperText: invalidDateFrom ? 'Enter a valid departure date.' : undefined,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <DatePicker
                  label="Departure date to"
                  value={formState.departureDateTo ? TimeService.toDayJs(formState.departureDateTo) : null}
                  onChange={(newValue: Dayjs | null) =>
                    setFormState((current) => ({ ...current, departureDateTo: newValue ? newValue.toDate() : null }))
                  }
                  minDate={
                    formState.departureDateFrom && !invalidDateFrom
                      ? TimeService.toDayJs(formState.departureDateFrom)
                      : undefined
                  }
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: invalidDateTo || invalidDateRange,
                      helperText: invalidDateTo
                        ? 'Enter a valid departure date.'
                        : invalidDateRange
                          ? 'End date must be on or after the start date.'
                          : undefined,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  SelectProps={{
                    multiple: true,
                    value: formState.cabinClass,
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as Array<CharterLoyaltyCabinClass>).map((value) => {
                          const label = CABIN_CLASS_OPTIONS.find((option) => option.value === value)?.label ?? value;
                          return <Chip key={value} size="small" label={label} />;
                        })}
                      </Box>
                    ),
                  }}
                  fullWidth
                  size="small"
                  label="Cabin class"
                  error={!formState.cabinClass.length}
                  helperText={!formState.cabinClass.length ? 'Select at least one cabin class.' : undefined}
                  onChange={(event) => {
                    const value = event.target.value;
                    setFormState((current) => ({
                      ...current,
                      cabinClass:
                        typeof value === 'string'
                          ? (value.split(',') as Array<CharterLoyaltyCabinClass>)
                          : (value as Array<CharterLoyaltyCabinClass>),
                    }));
                  }}
                >
                  {CABIN_CLASS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.airportFilterEnabled}
                    onChange={(event) =>
                      setFormState((current) => ({ ...current, airportFilterEnabled: event.target.checked }))
                    }
                  />
                }
                label="Filter by airports"
              />

              {formState.airportFilterEnabled &&
                formState.airportPairs.map((pair, index) => (
                  <Grid container spacing={2} key={index} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Departure airport"
                        value={pair.depAirport}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            airportPairs: current.airportPairs.map((p, i) =>
                              i === index ? { ...p, depAirport: event.target.value } : p,
                            ),
                          }))
                        }
                      >
                        {AIRPORT_OPTIONS.map((code) => (
                          <MenuItem key={code} value={code}>
                            {code}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Arrival airport"
                        value={pair.arrAirport}
                        onChange={(event) =>
                          setFormState((current) => ({
                            ...current,
                            airportPairs: current.airportPairs.map((p, i) =>
                              i === index ? { ...p, arrAirport: event.target.value } : p,
                            ),
                          }))
                        }
                      >
                        {AIRPORT_OPTIONS.map((code) => (
                          <MenuItem key={code} value={code}>
                            {code}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    <Grid size="auto">
                      <Button
                        variant="text"
                        color="primary"
                        size="small"
                        disabled={formState.airportPairs.length <= 1}
                        onClick={() =>
                          setFormState((current) => ({
                            ...current,
                            airportPairs: current.airportPairs.filter((_, i) => i !== index),
                          }))
                        }
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                ))}

              {formState.airportFilterEnabled && (
                <Box>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() =>
                      setFormState((current) => ({
                        ...current,
                        airportPairs: [
                          ...current.airportPairs,
                          { depAirport: AIRPORT_OPTIONS[0], arrAirport: AIRPORT_OPTIONS[1] },
                        ],
                      }))
                    }
                  >
                    Add airport pair
                  </Button>
                </Box>
              )}
            </Stack>

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={isSaving || loyaltyQuery.isFetching || !draftFilters}
              >
                Search
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardHeader title="Results" subheader={appliedFilterSummary} sx={{ pb: 0 }} />
        <CardContent>
          <Stack spacing={2}>
            {filtersChanged && (
              <Typography variant="body2" color="text.secondary">
                Filters changed. Search to update results.
              </Typography>
            )}
            {refreshFailed && (
              <Alert severity="warning">
                {editedRows.size ? 'Previous changes were saved' : 'Your changes were saved'}, but results could not be
                refreshed. Search again to refresh.
              </Alert>
            )}
            {!refreshFailed && loyaltyQuery.error ? <Alert severity="error">{loyaltyQuery.error.message}</Alert> : null}
            {!loyaltyQuery.error && !loyaltyQuery.isFetching && rows.length === 0 ? (
              <Alert severity="info">No loyalty point multiplier results found for selected filters.</Alert>
            ) : null}

            {(loyaltyQuery.isFetching || rows.length > 0) && (
              <Box
                sx={{
                  width: '100%',
                  height: rows.length <= MAX_AUTO_HEIGHT_ROWS ? 'auto' : 'clamp(320px, 55vh, 620px)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ flex: rows.length <= MAX_AUTO_HEIGHT_ROWS ? 'initial' : 1, minHeight: 0 }}>
                  <DataGrid
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    loading={loyaltyQuery.isFetching}
                    autoHeight={rows.length <= MAX_AUTO_HEIGHT_ROWS}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    hideFooterSelectedRowCount
                    disableRowSelectionOnClick
                    onCellClick={handleCellClick}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={(error) =>
                      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update cell.', {
                        variant: 'error',
                      })
                    }
                    getCellClassName={getCellClassName}
                    sx={{
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                      '& .cell-modified': { bgcolor: 'warning.light' },
                    }}
                  />
                </Box>
                {rows.length > 0 && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    gap={2}
                    sx={{
                      px: 2,
                      py: 1.5,
                      border: 1,
                      borderTop: 0,
                      borderColor: 'divider',
                      borderBottomLeftRadius: 1,
                      borderBottomRightRadius: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" aria-live="polite">
                      {editedRows.size
                        ? `${editedRows.size} ${editedRows.size === 1 ? 'departure' : 'departures'} with unsaved multiplier changes across all pages`
                        : 'Edit a multiplier in the table to make changes.'}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="text"
                        disabled={!editedRows.size || isSaving}
                        onClick={() => setPendingAction({ type: 'discard' })}
                      >
                        Discard edits
                      </Button>
                      <LoadingButton
                        variant="contained"
                        loading={isSaving}
                        onClick={handleSave}
                        disabled={!editedRows.size || loyaltyQuery.isFetching}
                      >
                        Save changes
                      </LoadingButton>
                    </Stack>
                  </Stack>
                )}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
      <Dialog
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        aria-labelledby="loyalty-discard-title"
        aria-describedby="loyalty-discard-description"
      >
        <DialogTitle id="loyalty-discard-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText id="loyalty-discard-description">
            Changes to {editedRows.size} {editedRows.size === 1 ? 'departure' : 'departures'} have not been saved.
            {pendingAction?.type === 'search'
              ? ' Discard them and show the search results?'
              : pendingAction?.type === 'navigate'
                ? ' Discard them and leave this page?'
                : ' Discard them and restore the last saved values?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)} autoFocus>
            Keep editing
          </Button>
          <Button variant="contained" onClick={confirmDiscard}>
            {pendingAction?.type === 'search'
              ? 'Discard and search'
              : pendingAction?.type === 'navigate'
                ? 'Discard and leave'
                : 'Discard changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
